import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, CheckCheck, Briefcase } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NOTIFICATIONS_CHANGED, emitNotificationsChanged } from "./candidate/NotificationsTab";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";

const POLL_MS = 60000;

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  return `há ${Math.floor(diff / 86400)} d`;
}

/** Sino de notificações (alertas de novas vagas) para utilizadores autenticados. */
export function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState(null);

  const refreshCount = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    api.get("/notifications", { params: { limit: 20 } }).then((r) => {
      setItems(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    }).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(() => { if (document.visibilityState === "visible") refreshCount(); }, POLL_MS);
    const h = () => { refreshCount(); if (open) loadList(); };
    window.addEventListener(NOTIFICATIONS_CHANGED, h);
    return () => { clearInterval(t); window.removeEventListener(NOTIFICATIONS_CHANGED, h); };
  }, [refreshCount, loadList, open]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${n.id}/read`).then(emitNotificationsChanged).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const readAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setUnread(0);
    setItems((prev) => (prev || []).map((n) => ({ ...n, read: true })));
    emitNotificationsChanged();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="nav-notifications-btn" variant="ghost" size="sm" className="relative rounded-full px-2" aria-label="Notificações">
          {unread > 0 ? <BellRing className="w-5 h-5 text-slate-800" /> : <Bell className="w-5 h-5 text-slate-600" />}
          {unread > 0 && (
            <span data-testid="nav-notifications-count"
              className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-amber-500 text-[10px] font-bold text-slate-950 flex items-center justify-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 overflow-hidden" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
          <p className="font-heading font-bold tracking-tight text-slate-900">Notificações</p>
          {unread > 0 && (
            <button type="button" data-testid="notifications-read-all" onClick={readAll}
              className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> Marcar todas como lidas
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items == null ? (
            <p className="px-4 py-6 text-sm text-slate-400 text-center">A carregar...</p>
          ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center" data-testid="notifications-empty">
              <Bell className="w-6 h-6 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Ainda não tens notificações.</p>
              <p className="text-xs text-slate-400 mt-1">Guarda uma pesquisa com alerta para seres avisado de novas vagas.</p>
            </div>
          ) : items.map((n) => (
            <button key={n.id} type="button" data-testid={`notification-item-${n.id}`} onClick={() => openItem(n)}
              className={`w-full text-left flex gap-3 px-4 py-3 border-b border-black/5 hover:bg-slate-50 transition-colors ${n.read ? "" : "bg-amber-50/60"}`}>
              <span className="mt-0.5 w-8 h-8 rounded-lg bg-slate-900 text-amber-400 flex items-center justify-center shrink-0">
                <Briefcase className="w-4 h-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm truncate ${n.read ? "text-slate-700" : "font-semibold text-slate-900"}`}>{n.title}</span>
                {n.body && <span className="block text-xs text-slate-500 truncate">{n.body}</span>}
                <span className="block text-[11px] text-slate-400 mt-0.5">
                  {n.source?.name ? `${n.source.name} · ` : ""}{timeAgo(n.created_at)}
                </span>
              </span>
              {!n.read && <span className="mt-2 w-2 h-2 rounded-full bg-amber-500 shrink-0" />}
            </button>
          ))}
        </div>
        {user && user.role === "candidate" && (
          <button type="button" data-testid="notifications-see-all"
            onClick={() => { setOpen(false); navigate("/candidato?tab=notifications"); }}
            className="w-full text-center text-xs font-semibold text-sky-700 hover:text-sky-900 hover:bg-slate-50 py-2.5 border-t border-black/5">
            Ver todas as notificações
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
